import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { SolfilmShowcase } from "@/components/sections/SolfilmShowcase";
import { BbnComparisonTables } from "@/components/sections/BbnComparisonTables";
import { EnergyCalculator } from "@/components/sections/EnergyCalculator";
import { TechDetails } from "@/components/TechDetails";
import { SpecComparisonTable } from "@/components/SpecComparisonTable";
import { CallbackRail } from "@/components/CallbackRail";
import { IMAGES } from "@/lib/images";
import borgebyHero from "@/assets/projekt/borgeby-uterum-2.jpg.asset.json";

export const FAQS: FaqItem[] = [
  {
    q: "Syns solfilmen utifrån?",
    a: "Knappast. Moderna filmer som 3M Prestige är nästan neutrala, så fasaden behåller sitt utseende och du ser klart genom fönstret. Vi kan visa provbitar i ditt eget fönster innan du bestämmer dig.",
  },
  {
    q: "Kan solfilm monteras i lägenhet och bostadsrätt?",
    a: "Ja, invändig solfilm kräver inga bygglov och påverkar inte fasaden. För bostadsrätter brukar styrelsen acceptera installation utan särskild ansökan eftersom inget sker på utsidan av fönstret. Vi rekommenderar ändå att du informerar styrelsen i förväg.",
  },
  {
    q: "Påverkar solfilm hemförsäkringen?",
    a: "Nej. Solfilm är ett invändigt tillägg som inte påverkar fastighetens grundkonstruktion eller försäkringsvillkor. Säkerhetsfilm kan i vissa fall till och med sänka premien. Kontakta ditt försäkringsbolag om du är osäker.",
  },
  {
    q: "Skyddar solfilm möbler och parkett från blekning?",
    a: "Ja. 3M-solfilm blockerar 99 % av UV-strålningen (UVA + UVB) som är huvudorsaken till att tyger, läder, trägolv och konst bleks. Synligt ljus och värme bidrar också till blekning, så filmen stoppar inte allt. Men blekningen minskar, med typiskt 4–5 gånger längre livslängd på utsatta inredningsdetaljer.",
  },
  {
    q: "Fungerar solfilm i uterum och glastak?",
    a: "Ja, och det är ett av de områden där effekten märks mest. Uterum och inglasade altaner blir extremt varma sommartid eftersom solen kommer in från flera vinklar samtidigt. Här använder vi ofta utvändig film med hög solvärmereduktion.",
  },
  {
    q: "Hur lång är livslängden på solfilmen?",
    a: "Invändig film håller normalt 15–20 år. Utvändig film håller 10–15 år beroende på exponering. Vi lämnar 10 års produktgaranti, men i praktiken byts filmen oftast först när huset säljs eller vid renovering, snarare än för att den slutat fungera.",
  },
  {
    q: "Hur lång tid tar installationen i en villa?",
    a: "En normal villa med 8–15 fönster installeras på en arbetsdag av två montörer. Vi behöver inte flytta möbler, men det ska vara fritt framför fönstren. Du kan vara hemma under arbetet, det är lukt- och dammfritt.",
  },
  {
    q: "Vad kostar solfilm för ett hem?",
    a: "För en standardvilla i Skåne ligger totalkostnaden typiskt mellan 12 000 och 35 000 kr beroende på fönsterantal, glasstorlek och vald filmserie. Vi kommer på ett kostnadsfritt hembesök och ger dig ett fast pris.",
  },
];

export const Route = createFileRoute("/solfilm-privat")({
  head: () => ({
    meta: [
      { title: "Solfilm för villa, lägenhet & uterum i Skåne | BBN Nordic" },
      { name: "description", content: "Solfilm för hemmet — svalare på sommaren, mindre bländning vid TV och dator, fullt UV-skydd för möbler. Diskret invändig montering med 10 års garanti. Kostnadsfritt hembesök i Skåne." },
      { property: "og:title", content: "Solfilm för privatpersoner" },
      { property: "og:description", content: "Solfilm för villa, lägenhet och uterum i Skåne — svalare hem utan att förlora utsikten." },
      { property: "og:url", content: "https://bbnnordic.se/solfilm-privat" },
      { property: "og:image", content: IMAGES.solfilm },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/solfilm-privat" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Solfilm för hemmet — villa, lägenhet & uterum",
          category: "Fönsterfilm / Solfilm",
          description: "3M solfilm för privatpersoner i Skåne. Svalare hem på sommaren, mindre bländning och fullt UV-skydd för möbler. 10 års produktgaranti.",
          image: IMAGES.solfilm,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          url: "/solfilm-privat",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "5000",
            priceSpecification: { "@type": "PriceSpecification", price: "5000", priceCurrency: "SEK" },
            availability: "https://schema.org/InStock",
            seller: { "@id": "/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),
      },


      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(FAQS)),
      },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      eyebrow="SOLFILM — PRIVAT"
      title="Solfilm för hemmet"
      subtitle="Med solfilm får du ett svalare hem på sommaren och mindre bländning vid TV och dator. Möbler, golv och konst får fullt UV-skydd, och du behåller utsikten."
      heroImage={borgebyHero.url}
      breadcrumbs={[{ label: "Solfilm privat" }]}
      sections={[
        {
          eyebrow: "VAD NI FÅR",
          heading: "Vad gör solfilm för ditt hem?",
          paragraphs: [
            "Solfilmen tar bort upp till tre fjärdedelar av solens värmeenergi vid glaset, innan den når sovrummet, vardagsrummet eller uterummet. Den minskar det direkta solljus som kan väcka dig kl 04:30 på sommaren och gör hemmakontoret användbart även mitt på dagen. Parkett, läder och konst skyddas mot blekning.",
            "Vi kommer hem till dig kostnadsfritt, mäter upp och lämnar fast pris. Vi går igenom vilken film som passar ditt hus. Sedan bestämmer du om du vill gå vidare.",
          ],
          bullets: [
            "3–6 °C svalare i utsatta rum under sommarens topptimmar",
            "Mindre bländning vid TV och skärm även mitt på dagen",
            "Textilier, läder och trägolv håller 4–5 gånger längre",
            "Kräver inte byggnadstillstånd och påverkar inte fasaden",
            "10 års produktgaranti från 3M",
          ],
          facts: [
            { value: "50 %", label: "Solvärme bort (TSER)", source: "3M Prestige 70" },
            { value: "99 %", label: "UV-blockering", source: "3M Prestige datablad" },
            { value: "68 %", label: "Synligt ljus släpps in", source: "3M Prestige 70 datablad" },
          ],
          extra: (
            <div className="space-y-6">
              <SpecComparisonTable
                tone="light"
                caption="3M Prestige 70 — VLT, TSER och UV-blockering enligt 3M:s datablad 1173225O."
                featureHeader="Egenskap"
                columns={[
                  { id: "p70", label: "Prestige 70", highlight: true },
                ]}
                rows={[
                  {
                    feature: "Synligt ljus släpps in (VLT)",
                    values: { p70: "68 %" },
                    source: "3M datablad 1173225O",
                    sourceHref: "/datablad",
                  },
                  {
                    feature: "Solvärme avvisad (TSER)",
                    values: { p70: "50 %" },
                    source: "3M datablad 1173225O",
                    sourceHref: "/datablad",
                  },
                  {
                    feature: "UV-blockering",
                    values: { p70: "99 %" },
                    source: "3M datablad 1173225O",
                  },
                  {
                    feature: "Bländningsreduktion",
                    values: { p70: "23 %" },
                    source: "3M datablad 1173225O",
                  },
                  {
                    feature: "Typiskt hemma-användning",
                    values: {
                      p70: "Vardagsrum, sovrum, uterum",
                    },
                  },
                ]}
              />
              <TechDetails
                summary="Läs mer om tekniken bakom 3M Prestige"
                sources={[
                  { label: "Se alla datablad", href: "/datablad" },
                ]}
              >
                <p>
                  3M Prestige är en nanofilm som reflekterar bort infraröd strålning
                  (värmen) men släpper igenom synligt ljus. Den innehåller ingen metall.
                  Fasaden behåller sitt utseende, mobilsignal och radiovågor passerar
                  fritt och filmen korroderar inte över tid.
                </p>
                <p>
                  Total Solar Energy Rejected (TSER) anger andelen av solens totala
                  energi som filmen blockerar. För högre TSER än Prestige 70 finns
                  exteriör silverfilm (t.ex. Solar Screen Silver 70 RS, upp till 78 %).
                </p>
              </TechDetails>
            </div>
          ),
          image: IMAGES.solfilm,
        },
        {
          eyebrow: "TYPISKA SITUATIONER",
          heading: "När hjälper solfilm i hemmet mest?",
          paragraphs: [
            "Varje vecka monterar vi solfilm hemma hos kunder i Skåne, från villor i Bjärred och Lomma till lägenheter i centrala Malmö och Lund. Det här är några av problemen vi ofta får hjälpa till med:",
          ],
          bullets: [
            "Vardagsrum med stora panoramafönster mot söder eller väster",
            "Sovrum där sommarmorgonen väcker dig kl 04:30 av direkt sol",
            "TV-rum där reflexer omöjliggör tittande dagtid",
            "Hemmakontor där skärmen bländas eller blir överhettad",
            "Uterum och inglasade altaner som blir för varma att vistas i juli",
            "Glastak och takfönster där solen står rakt in",
            "Hus med trägolv eller fina textilier som bleks av sol",
            "Lägenheter i toppvåningar med varmt sommarklimat",
          ],
          image: IMAGES.installer,
        },
        {
          eyebrow: "ALTERNATIV",
          heading: "Solfilm jämfört med markiser, persienner och nytt glas.",
          paragraphs: [
            "Blir det för varmt hemma kan du välja mellan solfilm, persienner, markiser och nya energiglas. Här är skillnaderna:",
          ],
          bullets: [
            "Solfilm — alltid aktiv, syns knappt, behåller utsikten, lägst totalkostnad",
            "Markiser — fungerar bra utvändigt men behöver fällas, känsliga för vind",
            "Persienner — blockerar utsikten när de är nere, ingen UV-funktion",
            "Nya energiglas — bästa tekniska prestandan men 5–10× högre kostnad och bygglovsfrågor",
          ],
          image: IMAGES.solfilmHero,
        },
      ]}
      related={[
        { to: "/3m-prestige", label: "3M Prestige", desc: "Solfilm med klar sikt och hög värmereduktion." },
        { to: "/3m-sakerhetsfilm-med-solfilm", label: "Kombifilm — säkerhet + solfilm", desc: "Två funktioner i en film." },
        { to: "/3m-sakerhetsfilm-utan-solfilm", label: "Klar säkerhetsfilm", desc: "Splitterskydd som inte tonar glaset." },
        { to: "/3m-ultra-sakerhetsfilm", label: "3M Ultra säkerhetsfilm", desc: "Maximalt inbrottsskydd." },
        { to: "/radgivning", label: "Gratis rådgivning", desc: "Vi hjälper dig välja rätt film." },
        { to: "/offert", label: "Begär offert", desc: "Kostnadsfri offert utan bindning." },
        { to: "/om-fonsterfilm", label: "Om fönsterfilm", desc: "Vad film är, hur den fungerar och när insynsskydd är rätt val." },
      ]}
      priorityContent={
        <>
          <EnergyCalculator variant="section" contactHref="/offert" />
          <SolfilmShowcase profile="privat" />
          <BbnComparisonTables />
        </>
      }
    >
      <FaqSection
        heading="Vanliga frågor från villaägare och bostadsrättsinnehavare."
        items={FAQS}
      />
      <CallbackRail product="Solfilm" />
    </ContentPage>
  ),
});
